import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Button } from '@/components/ui/button';
import { Plus, Minus, Award, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';

// --- Configurações do Jogo ---
const ROWS = 3;
const COLS = 5;
const REEL_STRIP_LENGTH = 30; // Comprimento da "tira" de símbolos para cada rolo
const SYMBOL_HEIGHT_PX = 96; // Altura padrão do símbolo em px (para telas menores)
const SYMBOL_HEIGHT_PX_MD = 144; // Altura do símbolo em px (para telas médias e maiores)
const SYMBOLS = ['🍒', '🍋', '🍊', '🍉', '🔔', '⭐', '💎', '🟪', '💰']; // Usando 🟪 no lugar de 𝟳
const PAYOUTS: { [key: string]: { [key: number]: number } } = {
  '🍒': { 3: 5, 4: 10, 5: 20 }, '🍋': { 3: 5, 4: 10, 5: 20 }, '🍊': { 3: 5, 4: 10, 5: 20 },
  '🍉': { 3: 10, 4: 20, 5: 50 }, '🔔': { 3: 15, 4: 30, 5: 75 }, '⭐': { 3: 20, 4: 40, 5: 100 },
  '💎': { 3: 50, 4: 100, 5: 250 }, '🟪': { 3: 100, 4: 250, 5: 1000 }, // Usando 🟪 no lugar de 𝟳
};
const BET_AMOUNTS = [1, 2, 5, 10, 20, 50, 100];
const COIN_VALUES_MAP: { [key: number]: number[] } = {
  1: [1, 2, 3, 5, 10], 2: [2, 4, 6, 10, 20], 5: [5, 10, 15, 25, 50],
  10: [10, 20, 30, 50, 100], 20: [20, 40, 60, 100, 200], 50: [50, 100, 150, 250, 500],
  100: [100, 200, 300, 500, 1000],
};

const getRandomCoinValue = (bet: number): number => {
  const values = COIN_VALUES_MAP[bet] || [bet];
  return values[Math.floor(Math.random() * values.length)];
};

// --- Sub-componente para renderizar um único rolo ---
interface ReelDisplayProps {
  symbols: (string | number | null)[];
  isSpinning: boolean;
  finalSymbols: (string | number | null)[];
  reelIndex: number;
  isBonusMode: boolean;
  winningCells: [number, number][];
}

const ReelDisplay: React.FC<ReelDisplayProps> = ({ symbols, isSpinning, finalSymbols, reelIndex, isBonusMode, winningCells }) => {
  const reelRef = React.useRef<HTMLDivElement>(null);
  const symbolHeight = 96; // Altura padrão do símbolo em px
  const symbolHeightMd = 144; // Altura do símbolo em px para md e acima

  const getSymbolHeight = () => {
    // Adapta a altura do símbolo com base na largura da tela
    return window.innerWidth >= 768 ? symbolHeightMd : symbolHeight;
  };

  useEffect(() => {
    const currentSymbolHeight = getSymbolHeight();
    if (reelRef.current && !isSpinning) {
      // Calcula a posição final para mostrar os 3 símbolos corretos
      const finalPosition = -(REEL_STRIP_LENGTH - ROWS) * currentSymbolHeight;
      reelRef.current.style.transition = 'transform 0.5s ease-out';
      reelRef.current.style.transform = `translateY(${finalPosition}px)`;
    }
  }, [isSpinning, finalSymbols, symbols.length]);

  const renderSymbol = (symbol: string | number | null, rowIndex: number) => {
    const isBonusSymbol = isBonusMode && typeof symbol === 'number';
    const isWinning = winningCells.some(([r, c]) => r === rowIndex && c === reelIndex);

    return (
      <div
        key={rowIndex}
        className={cn(
          "w-full flex-shrink-0 flex items-center justify-center text-4xl md:text-6xl rounded-md",
          `h-[${symbolHeight}px] md:h-[${symbolHeightMd}px]`, // Altura fixa para cada símbolo
          isWinning ? 'bg-primary/50 scale-110 shadow-glow' : 'bg-secondary',
          isBonusSymbol && 'bg-yellow-500/50 animate-pulse text-yellow-200 font-bold',
          isBonusMode && symbol === null && 'bg-secondary/50'
        )}
      >
        {isBonusSymbol ? symbol.toFixed(2) : symbol}
      </div>
    );
  };

  return (
    <div className="flex-1 h-full overflow-hidden">
      <div
        ref={reelRef}
        className={cn(
          "flex flex-col",
          isSpinning && "animate-spin-vertical"
        )}
        style={{
          // Posição inicial para o giro (fora da tela)
          transform: isSpinning ? `translateY(-${(REEL_STRIP_LENGTH - ROWS) * getSymbolHeight()}px)` : `translateY(-${(REEL_STRIP_LENGTH - ROWS) * getSymbolHeight()}px)`,
          transition: isSpinning ? 'none' : 'transform 0.5s ease-out',
          height: `${REEL_STRIP_LENGTH * getSymbolHeight()}px`, // Altura total da tira
        }}
      >
        {isSpinning ? (
          // Durante o giro, mostra uma tira longa de símbolos aleatórios
          symbols.map((symbol, i) => renderSymbol(symbol, i))
        ) : (
          // Quando parado, mostra apenas os 3 símbolos finais
          finalSymbols.map((symbol, i) => renderSymbol(symbol, i))
        )}
      </div>
    </div>
  );
};


// --- Componente Principal do Jogo ---
const MySlotGame: React.FC = () => {
  const [reels, setReels] = useState<string[][]>(() => Array(COLS).fill([]).map(() => Array(REEL_STRIP_LENGTH).fill(SYMBOLS[0])));
  const [spinningReels, setSpinningReels] = useState<boolean[]>(Array(COLS).fill(false));
  const [balance, setBalance] = useState(10000);
  const [betIndex, setBetIndex] = useState(2);
  const [message, setMessage] = useState('Bem-vindo! Faça sua aposta.');
  const [winningCells, setWinningCells] = useState<[number, number][]>([]);
  const [isBonus, setIsBonus] = useState(false);
  const [reSpinsLeft, setReSpinsLeft] = useState(0);
  const [bonusReels, setBonusReels] = useState<(number | null)[][]>([]);
  const [totalBets, setTotalBets] = useState(0);
  const [isAutoplaying, setIsAutoplaying] = useState(false);

  const currentBet = BET_AMOUNTS[betIndex];
  const isSpinning = useMemo(() => spinningReels.some(s => s), [spinningReels]);

  const generateReelStrip = () => Array.from({ length: REEL_STRIP_LENGTH }, () => SYMBOLS[Math.floor(Math.random() * SYMBOLS.length)]);

  useEffect(() => {
    setReels(Array(COLS).fill(null).map(() => generateReelStrip()));
  }, []);

  const finishSpin = useCallback((finalReels: string[][]) => {
    const finalResult = finalReels.map(reel => reel.slice(0, ROWS));
    const totalCoins = finalResult.flat().filter(symbol => symbol === '💰').length;

    if (totalCoins >= 6 || (totalBets >= 10000 && totalCoins > 0)) {
      if (totalBets >= 10000) {
        setMessage('BÔNUS DE LEALDADE!');
        setTotalBets(0);
      }
      setIsBonus(true);
      return;
    }

    let totalWin = 0;
    const winningCombinations: [number, number][] = [];
    for (let r = 0; r < ROWS; r++) {
      const symbol = finalResult[0][r];
      let count = 1;
      for (let c = 1; c < COLS; c++) {
        if (finalResult[c][r] === symbol) count++;
        else break;
      }
      if (count >= 3) {
        totalWin += (PAYOUTS[symbol]?.[count] || 0) * currentBet;
        for (let c = 0; c < count; c++) winningCombinations.push([r, c]);
      }
    }

    if (totalWin > 0) {
      setBalance(prev => prev + totalWin);
      setMessage(`Você ganhou ${totalWin.toFixed(2)}!`);
      setWinningCells(winningCombinations);
    } else {
      setMessage('Tente novamente!');
    }
  }, [currentBet, totalBets]);

  const handleSpin = useCallback(() => {
    if (isSpinning || isBonus) return;
    if (balance < currentBet) {
      setMessage('Saldo insuficiente!');
      setIsAutoplaying(false);
      return;
    }

    setBalance(prev => prev - currentBet);
    setTotalBets(prev => prev + currentBet);
    setWinningCells([]);
    setMessage('');
    setSpinningReels(Array(COLS).fill(true));

    const finalReels = reels.map(() => generateReelStrip());

    for (let i = 0; i < COLS; i++) {
      setTimeout(() => {
        setReels(prev => {
          const newReels = [...prev];
          newReels[i] = finalReels[i];
          return newReels;
        });
        setSpinningReels(prev => {
          const newSpinning = [...prev];
          newSpinning[i] = false;
          return newSpinning;
        });
        if (i === COLS - 1) {
          setTimeout(() => finishSpin(finalReels), 500);
        }
      }, 1000 + i * 200);
    }
  }, [isSpinning, isBonus, balance, currentBet, reels, finishSpin]);

  const startBonusGame = useCallback(() => {
    setMessage('BÔNUS! Segure e Ganhe!');
    setReSpinsLeft(3);
    const finalResult = reels.map(reel => reel.slice(0, ROWS));
    const newBonusReels = finalResult[0].map((_, colIndex) => finalResult.map(row => row[colIndex]));
    const preparedBonusReels = newBonusReels.map(row => row.map(symbol => (symbol === '💰' ? getRandomCoinValue(currentBet) : null)));
    setBonusReels(preparedBonusReels);
    runReSpins(preparedBonusReels);
  }, [reels, currentBet]);

  const runReSpins = async (currentReels: (number | null)[][]) => {
    let spins = 3;
    let tempReels = currentReels.map(row => [...row]);
    for (let s = spins; s > 0; s--) {
      setReSpinsLeft(s);
      await new Promise(res => setTimeout(res, 800));
      let newCoinLanded = false;
      const nextReels = tempReels.map(row => [...row]);
      for (let r = 0; r < ROWS; r++) {
        for (let c = 0; c < COLS; c++) {
          if (nextReels[r][c] === null && Math.random() < 0.2) {
            nextReels[r][c] = getRandomCoinValue(currentBet);
            newCoinLanded = true;
          }
        }
      }
      tempReels = nextReels;
      setBonusReels(tempReels);
      if (newCoinLanded) s = 4;
    }
    endBonusGame(tempReels);
  };

  const endBonusGame = (finalBonusReels: (number | null)[][]) => {
    const bonusWin = finalBonusReels.flat().reduce((sum: number, val) => sum + (val || 0), 0);
    if (bonusWin > 0) {
      setMessage(`Bônus finalizado! Você ganhou ${bonusWin.toFixed(2)}!`);
      setBalance(prev => prev + bonusWin);
    } else {
      setMessage('Bônus finalizado! Mais sorte na próxima vez!');
    }
    setTimeout(() => {
      setIsBonus(false);
      setMessage('Faça sua aposta.');
    }, 3000);
  };

  useEffect(() => {
    if (isBonus) {
      const timer = setTimeout(startBonusGame, 3000);
      return () => clearTimeout(timer);
    }
  }, [isBonus, startBonusGame]);

  useEffect(() => {
    let autoplayTimeout: any;
    if (isAutoplaying && !isSpinning && !isBonus) {
      autoplayTimeout = setTimeout(handleSpin, 1500);
    }
    return () => clearTimeout(autoplayTimeout);
  }, [isAutoplaying, isSpinning, isBonus, handleSpin]);

  const changeBet = (direction: 'up' | 'down') => {
    if (isSpinning || isBonus) return;
    if (direction === 'up' && betIndex < BET_AMOUNTS.length - 1) setBetIndex(betIndex + 1);
    if (direction === 'down' && betIndex > 0) setBetIndex(betIndex - 1);
  };

  const toggleAutoplay = () => setIsAutoplaying(prev => !prev);

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-900 text-white p-4">
      <div className="w-full max-w-3xl bg-gray-800 rounded-2xl shadow-2xl border-4 border-primary/50 p-6">
        <div className="flex justify-center gap-2 bg-black/50 p-4 rounded-lg border-2 border-secondary mb-4 overflow-hidden h-[288px] md:h-[432px]">
          {reels.map((reel, reelIndex) => (
            <div key={reelIndex} className="flex-1 h-full overflow-hidden">
              <div
                className={cn(
                  "flex flex-col",
                  isSpinning && "animate-spin-vertical"
                )}
                style={{
                  transform: `translateY(-${(REEL_STRIP_LENGTH - ROWS) * SYMBOL_HEIGHT_PX}px)`,
                  transition: isSpinning ? 'none' : 'transform 0.5s ease-out',
                  height: `${REEL_STRIP_LENGTH * SYMBOL_HEIGHT_PX}px`,
                }}
              >
                {reel.map((symbol, i) => (
                  <div key={i} className="w-full h-[96px] flex-shrink-0 flex items-center justify-center text-4xl md:text-6xl">
                    {symbol}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div className="h-8 text-center mb-4 text-lg font-semibold text-accent">{message}</div>
        <div className="grid grid-cols-3 items-center gap-2 md:gap-4 bg-gray-900/50 p-4 rounded-lg">
          <div className="flex flex-col items-center">
            <span className="text-xs md:text-sm text-muted-foreground">APOSTA</span>
            <div className="flex items-center space-x-1 md:space-x-2 mt-1">
              <Button variant="secondary" size="icon" onClick={() => changeBet('down')} disabled={isSpinning || isBonus}><Minus className="w-4 h-4 md:w-5 md:h-5" /></Button>
              <span className="text-lg md:text-xl font-bold w-12 md:w-16 text-center">{currentBet}</span>
              <Button variant="secondary" size="icon" onClick={() => changeBet('up')} disabled={isSpinning || isBonus}><Plus className="w-4 h-4 md:w-5 md:h-5" /></Button>
            </div>
          </div>
          <div className="flex flex-col items-center justify-center">
            <Button size="lg" className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-gradient-primary text-xl md:text-2xl font-bold shadow-lg hover:shadow-glow transition-all duration-300 disabled:opacity-50" onClick={handleSpin} disabled={isSpinning || isBonus}>
              {isSpinning ? <Loader2 className="animate-spin" /> : 'GIRAR'}
            </Button>
            <Button variant="link" className="mt-2 text-xs text-muted-foreground" onClick={toggleAutoplay}>
              {isAutoplaying ? 'Parar Auto' : 'Jogo Automático'}
            </Button>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-xs md:text-sm text-muted-foreground">SALDO</span>
            <span className="text-lg md:text-2xl font-bold mt-1">{balance.toFixed(2)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MySlotGame;
